import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { Input } from "@frui.ts/bootstrap";
import BaseAccountNumberInput from "controls/finance/baseAccountNumberInput";
import CzechAccountInput from "controls/finance/czechAccountInput";
import IbanInput from "controls/finance/ibanInput";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import TransferPaymentType from "entities/transferPaymentType";
import { observer } from "mobx-react-lite";
import React from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { translateGeneral } from "utils/localizations";
import type { BaseRefundViewModel } from "viewModels/callCenter/search/charges/baseRefundViewModel";
import BankCodeInputGroup from "views/common/bankCodeInputGroup";
import CountrySelect from "views/common/countrySelect";
import PossibleSEPAPaymentWarning from "views/common/possibleSEPAPaymentWarning";

function RefundPaymentForm({ vm }: { vm: BaseRefundViewModel }) {
  return (
    <>
      <Form.Row>
        <FormGroup as={Col} md={6} target={vm.item} property="transferType">
          <FormLabel>{vm.translateAttribute("customerRefundCandidateRequest", "transferType")}</FormLabel>
          <AdvancedSelect items={vm.transferTypeOptions} />
        </FormGroup>
        {vm.item.transferType === TransferPaymentType.Foreign && (
          <FormGroup as={Col} md={6} target={vm.item.counterpartyBank} property="codeType">
            <FormLabel>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "codeType")}</FormLabel>
            <AdvancedSelect allowEmpty items={vm.bankCodeOptions} />
          </FormGroup>
        )}
      </Form.Row>

      <Form.Row>
        {vm.item.transferType === TransferPaymentType.Foreign || vm.item.transferType === TransferPaymentType.Sepa ? (
          <>
            {vm.item.transferType === TransferPaymentType.Sepa ? (
              <FormGroup as={Col} md={6} target={vm.item.counterpartyBank} property="accountNumber">
                <FormLabel>{translateGeneral("finance.iban")}</FormLabel>
                <IbanInput onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
              </FormGroup>
            ) : (
              <FormGroup as={Col} md={6} target={vm.item.counterpartyBank} property="accountNumber">
                <FormLabel>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "accountNumber")}</FormLabel>
                <BaseAccountNumberInput onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
              </FormGroup>
            )}
            <BankCodeInputGroup codeType={vm.item.counterpartyBank.codeType} target={vm.item.counterpartyBank} />
          </>
        ) : (
          <FormGroup as={Col} target={vm.item} property="counterpartyBank">
            <FormLabel required={true}>{vm.translateAttribute("customerRefundCandidateRequest", "counterpartyBank")}</FormLabel>
            <CzechAccountInput onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
          </FormGroup>
        )}
      </Form.Row>
      {vm.item.transferType === TransferPaymentType.Foreign && (
        <>
          <Form.Row>
            <FormGroup as={Col} target={vm.item.counterpartyBank} property="name">
              <FormLabel>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "name")}</FormLabel>
              <Input />
            </FormGroup>
          </Form.Row>
          <Form.Row>
            <FormGroup as={Col} md={6} target={vm.item.counterpartyBank} property="cityAddress">
              <FormLabel>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "cityAddress")}</FormLabel>
              <Input />
            </FormGroup>
            <FormGroup as={Col} md={6} target={vm.item.counterpartyBank} property="countryId">
              <FormLabel>{vm.translateAttribute("transferRefundCounterpartyBankRequest", "countryId")}</FormLabel>
              <CountrySelect allowEmpty vm={vm.countrySelectViewModel} />
            </FormGroup>
            <PossibleSEPAPaymentWarning show={vm.showSEPAWarning} />
          </Form.Row>
        </>
      )}
      {(vm.item.transferType === TransferPaymentType.Sepa || vm.item.transferType === TransferPaymentType.Foreign) && (
        <>
          <hr className="mt-0" />
          <Form.Row>
            <FormGroup as={Col} md={6} target={vm.item} property="counterpartyName">
              <FormLabel>{vm.translateAttribute("customerRefundCandidateRequest", "counterpartyName")}</FormLabel>
              <Input />
            </FormGroup>
            <FormGroup as={Col} md={6} target={vm.item} property="counterpartyCountryId">
              <FormLabel>{vm.translateAttribute("customerRefundCandidateRequest", "counterpartyCountryId")}</FormLabel>
              <CountrySelect allowEmpty vm={vm.countrySelectViewModel} onlySEPACountries={vm.isSEPAPayment} />
            </FormGroup>
            <FormGroup as={Col} md={6} target={vm.item} property="counterpartyStreetAddress">
              <FormLabel>{vm.translateAttribute("customerRefundCandidateRequest", "counterpartyStreetAddress")}</FormLabel>
              <Input />
            </FormGroup>
            <FormGroup as={Col} md={6} target={vm.item} property="counterpartyCityAddress">
              <FormLabel>{vm.translateAttribute("customerRefundCandidateRequest", "counterpartyCityAddress")}</FormLabel>
              <Input />
            </FormGroup>
          </Form.Row>
        </>
      )}
      <Form.Row>
        <FormGroup as={Col} target={vm.item} property="additionalAvMessage">
          <FormLabel>{vm.translateAttribute("customerRefundCandidateRequest", "additionalAvMessage")}</FormLabel>
          <Input />
        </FormGroup>
      </Form.Row>
    </>
  );
}

export default observer(RefundPaymentForm);
